import { type DocumentConnectorItem, type WorkspaceNode } from '../../store/documentConnectors/documentConnectors'
import { type SelectOption } from '../SelectDropdown/SelectDropdown'
import { type FileBrowserItem } from './types'

export const formatFileSize = (bytes?: number): string => {
  if (bytes == null) {
    return ''
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const FILE_ICON_BY_EXTENSION: Record<string, string> = {
  pdf: 'bi-file-earmark-pdf-fill text-danger',
  doc: 'bi-file-earmark-word-fill text-primary',
  docx: 'bi-file-earmark-word-fill text-primary',
  xls: 'bi-file-earmark-spreadsheet-fill text-success',
  xlsx: 'bi-file-earmark-spreadsheet-fill text-success',
  csv: 'bi-file-earmark-spreadsheet-fill text-success',
  ppt: 'bi-file-earmark-slides-fill zocks-text-warning-text',
  pptx: 'bi-file-earmark-slides-fill zocks-text-warning-text',
  png: 'bi-file-earmark-image-fill zocks-text-primary',
  jpg: 'bi-file-earmark-image-fill zocks-text-primary',
  jpeg: 'bi-file-earmark-image-fill zocks-text-primary',
  gif: 'bi-file-earmark-image-fill zocks-text-primary',
  svg: 'bi-file-earmark-image-fill zocks-text-primary',
  webp: 'bi-file-earmark-image-fill zocks-text-primary',
  mp4: 'bi-file-earmark-play-fill zocks-text-info',
  mov: 'bi-file-earmark-play-fill zocks-text-info',
  avi: 'bi-file-earmark-play-fill zocks-text-info',
  mkv: 'bi-file-earmark-play-fill zocks-text-info',
  mp3: 'bi-file-earmark-music-fill zocks-text-info',
  wav: 'bi-file-earmark-music-fill zocks-text-info',
  aac: 'bi-file-earmark-music-fill zocks-text-info',
  zip: 'bi-file-earmark-zip-fill zocks-text-gray-600',
  rar: 'bi-file-earmark-zip-fill zocks-text-gray-600',
  gz: 'bi-file-earmark-zip-fill zocks-text-gray-600',
  tar: 'bi-file-earmark-zip-fill zocks-text-gray-600',
  txt: 'bi-file-earmark-text-fill zocks-text-gray-600',
  md: 'bi-file-earmark-text-fill zocks-text-gray-600',
  js: 'bi-file-earmark-code-fill zocks-text-gray-600',
  ts: 'bi-file-earmark-code-fill zocks-text-gray-600',
  py: 'bi-file-earmark-code-fill zocks-text-gray-600',
  json: 'bi-file-earmark-code-fill zocks-text-gray-600',
  html: 'bi-file-earmark-code-fill zocks-text-gray-600',
  css: 'bi-file-earmark-code-fill zocks-text-gray-600',
}

const DEFAULT_FILE_ICON = 'bi-file-earmark zocks-text-gray-500'

export const getFileIconClass = (name: string): string => {
  const ext = name.split('.').pop()?.toLowerCase()
  return (ext && FILE_ICON_BY_EXTENSION[ext]) || DEFAULT_FILE_ICON
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) {
    return ''
  }
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export const formatUpdated = (item: FileBrowserItem): string => {
  const date = formatDate(item.modifiedAt)
  if (!date) {
    return ''
  }
  return item.modifiedBy ? `${date} by ${item.modifiedBy}` : date
}

export const formatSizeColumn = (item: FileBrowserItem): string => {
  if (item.type === 'folder') {
    return item.itemCount != null ? `${item.itemCount} file${item.itemCount !== 1 ? 's' : ''}` : ''
  }
  return formatFileSize(item.size)
}

export const buildFolderPathSegments = (folder: Pick<DocumentConnectorItem, 'name' | 'ancestorFolders'>): string[] => [
  ...(folder.ancestorFolders ?? []).map((ancestor) => ancestor.name ?? ''),
  folder.name,
]

export interface FormattedFolderPath {
  display: string
  full: string
  isTruncated: boolean
}

export const formatFolderPath = (segments: string[], maxSegments = 4): FormattedFolderPath => {
  const full = segments.join('/')
  if (segments.length <= maxSegments) {
    return { display: full, full, isTruncated: false }
  }
  const head = maxSegments >= 2 ? [segments[0], '...'] : ['...']
  const tail = segments.slice(-Math.max(maxSegments - 1, 1))
  return { display: [...head, ...tail].join('/'), full, isTruncated: true }
}

export const getNextFileName = (name: string): string => {
  const dot = name.lastIndexOf('.')
  const stem = dot > 0 ? name.slice(0, dot) : name
  const ext = dot > 0 ? name.slice(dot) : ''
  const match = stem.match(/^(.*) \((\d+)\)$/)
  const nextStem = match ? `${match[1]} (${Number(match[2]) + 1})` : `${stem} (1)`
  return `${nextStem}${ext}`
}

export const getAvailableFileName = (desired: string, existingNames: string[]): string => {
  const taken = new Set(existingNames.map((n) => n.toLowerCase()))
  const resolve = (candidate: string): string => (taken.has(candidate.toLowerCase()) ? resolve(getNextFileName(candidate)) : candidate)
  return resolve(desired)
}

export const workspaceNodesToOptions = (nodes: WorkspaceNode[], parentName?: string): SelectOption[] =>
  nodes.flatMap((node) => {
    const pathLabel = parentName ? `${parentName} / ${node.name}` : node.name
    const children = node.children ?? []
    if (children.length > 0) {
      return [{ id: `__group__${node.id}`, label: node.name, disabled: true }, ...workspaceNodesToOptions(children, pathLabel)]
    }
    return node.selectable ? [{ id: node.id, label: pathLabel, displayLabel: node.name }] : []
  })

export const findWorkspacePathLabel = (nodes: WorkspaceNode[], target: string, parentName?: string): string | null =>
  nodes.reduce<string | null>((found, node) => {
    if (found) {
      return found
    }
    const pathLabel = parentName ? `${parentName} / ${node.name}` : node.name
    return node.id === target ? pathLabel : findWorkspacePathLabel(node.children ?? [], target, pathLabel)
  }, null)
